/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import { Platform } from 'react-native';
import type { FirebaseApp } from 'firebase/app';

/**
 * Both prefixes: Next.js inlines only NEXT_PUBLIC_*, Expo/Metro only
 * EXPO_PUBLIC_*, so exactly one side is ever populated.
 */
export const buildFirebaseConfig = () => ({
  apiKey:
    process.env.NEXT_PUBLIC_FIREBASE_API_KEY ??
    process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
  authDomain:
    process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN ??
    process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId:
    process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID ??
    process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket:
    process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET ??
    process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId:
    process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID ??
    process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId:
    process.env.NEXT_PUBLIC_FIREBASE_APP_ID ??
    process.env.EXPO_PUBLIC_FIREBASE_APP_ID,
  measurementId:
    process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID ??
    process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID,
});

/** The shared app instance, or null when Firebase cannot be used here. */
export const getFirebaseApp = async (): Promise<FirebaseApp | null> => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;

  const config = buildFirebaseConfig();
  // The minimum Firebase needs to resolve a project.
  if (!config.apiKey || !config.projectId || !config.appId) return null;

  try {
    // Lazy: keeps the SDK out of the initial chunk and off apps without the peer.
    const { initializeApp, getApp, getApps } = await import('firebase/app');
    return getApps().length === 0 ? initializeApp(config) : getApp();
  } catch {
    return null;
  }
};
